/* eslint-disable */
import React, { memo, useMemo, useState, useEffect, useRef, forwardRef } from 'react';
import { List, useListRef } from 'react-window';
import { parseMoney } from '../utils';
import LoaderGraphic from '../utils/LoaderGraphic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListUl } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const MappingTable = forwardRef(({
  lastViewedRow,
  setLastViewedRow,
  rowHeights,
  rowTops,
  isFetching,
  fdrs,
  tableSortYears,
  tableSort,
  handleRowSort,
  handleNewNbayesValue,
  hideHistorics,
  user_type,
  naics_code,
  fdu_id,
  fiscalYearEnd,
  years,
  map_list,
  assets_list,
  liabilities_list,
  equity_list,
  revenues_list,
  expenses_list,
  updateRows,
  totals,
  values,
  dragListener
}, ref) => {
  const headerRef = useRef();
  const bodyRef = useRef();
  const listRef = useListRef(null);

  useEffect(() => {  
    if (listRef.current && fdrs.length > 0 && lastViewedRow < fdrs.length) {
      requestAnimationFrame(() => {
        if (listRef.current) {
          // console.log('[scroll-debug] scrollTop BEFORE:', listRef.current.element?.scrollTop);
          listRef.current.scrollToRow({ index: lastViewedRow, align: 'start' });
          // requestAnimationFrame(() => {
            // console.log('[scroll-debug] scrollTop AFTER (2nd frame):', listRef.current?.element?.scrollTop);
          // });
        }
      });
    }
  }, [fdrs.length]);

  useEffect(() => {
    const headerEl = headerRef.current;
    const bodyEl = bodyRef.current;

    if (!headerEl || !bodyEl) return;

    let isSyncing = false;

    const syncFromBody = () => {
      if (isSyncing) return;
      isSyncing = true;
      headerEl.scrollLeft = bodyEl.scrollLeft;
      isSyncing = false;
    };

    const syncFromHeader = () => {
      if (isSyncing) return;
      isSyncing = true;
      bodyEl.scrollLeft = headerEl.scrollLeft;
      isSyncing = false;
    };

    bodyEl.addEventListener('scroll', syncFromBody);
    headerEl.addEventListener('scroll', syncFromHeader);

    return () => {
      bodyEl.removeEventListener('scroll', syncFromBody);
      headerEl.removeEventListener('scroll', syncFromHeader);
    };
  }, []);

  const [bestNbayesMatches, setBestNbayesMatches] = useState({
    rowId: null,
    display: false,
    data: {}
  })

  const matchAbbr = {
    "context account type":  "context acct type",
    "context nonstandard account number": "context nonstd acct num",
    "context standard account number": "context std acct num",
    "historic": "historic",
    "historic context account type":  "hist context acct type",
    "historic context nonstandard account number": "hist context nonstd acct num",
    "historic context standard account number": "hist context std acct num",
    "historic nbayes": "hist nbayes",
    "exact": "exact",
    "exact_firm_naics_account_number_standard": "exact firm naics act num",
    "exact_firm_naics_account_number": "exact firm naics act num",
    "exact_firm_naics_standard": "exact firm naics",
    "exact_firm_naics": "exact firm naics",
    "exact_coa_name": "exact account name",
    "exact_firm_naics_account_type": "exact firm naics act type",
    "exact_firm_account_type": "exact firm act type",
    "exact_firm_standard": "exact firm",
    "exact_firm": "exact firm",
    "exact_all_account_type": "exact all act type",
    "exact_all_standard": "exact all",
    "exact_all": "exact all",
    "exact_by_words_account_type": "exact words act type",
    "exact_by_words_standard": "exact words",
    "exact_by_words_strong": "exact words strong",
    "exact_by_words": "exact words",
    "section match": "section match",
    "nbayes": "nbayes",
    "ai": "ai"
  }

  const sortColsAdmin = {
    'match_method': { 'name': 'Match Method', 'className': 'match-method-col' },
    'account_number': { 'name': 'Account', 'className': 'account-name-col' },
    'account_type': { 'name': 'Given Type', 'className': 'given-type-col' },
    'extracted_account': { 'name': 'Given', 'className': 'given-col' },
    'historic_matched': { 'name': 'Historical', 'className': 'historical-col' },
    'xlsx_matched': { 'name': 'Best Match', 'className': 'best-match-col' },
    'nbayes_matched': { 'name': 'Alternatives', 'className': 'alternatives-col' },
    'ai_match': { 'name': 'AI Match', 'className': 'alternatives-col' },
    'final_account': { 'name': 'Final', 'className': 'final-col' },
  }

  const sortCols = {
    'account_number': { 'name': 'Account', 'className': 'account-name-col' },
    'account_type': { 'name': 'Given Type', 'className': 'given-type-col' },
    'extracted_account': { 'name': 'Given', 'className': 'given-col' },
    'historic_matched': { 'name': 'Historical', 'className': 'historical-col' },
    'xlsx_matched': { 'name': 'Best Match', 'className': 'best-match-col' },
    'nbayes_matched': { 'name': 'Alternatives', 'className': 'alternatives-col' },
    'ai_match': { 'name': 'AI Match', 'className': 'alternatives-col' },
    'final_account': { 'name': 'Final', 'className': 'final-col' },
  }

  const accountTypeColors = {
    'Asset': 'asset-cell',
    'A': 'asset-cell',
    'CA': 'asset-cell',
    'NA': 'asset-cell',
    'Liability': 'liability-cell',
    'L': 'liability-cell',
    'CL': 'liability-cell',
    'NL': 'liability-cell',
    'Equity': 'equity-cell',
    'Q': 'equity-cell',
    'EQ': 'equity-cell',
    'Revenue': 'revenue-cell',
    'R': 'revenue-cell',
    'REV': 'revenue-cell',
    'Expense': 'expense-cell',
    'E': 'expense-cell',
    'COS': 'expense-cell',
    'OE': 'expense-cell',
    'OPX': 'expense-cell',
    'OI': 'expense-cell',
    'Balance Sheet': 'balance-sheet-cell',
    'Income Statement': 'pnl-cell',
    'P and L': 'pnl-cell',
    'Profit and Loss': 'pnl-cell'
  }

  const mapListAccountTypeColor = (accountName, selected = false) => {
    let backgroundColor = ''
    if (accountName && assets_list.includes(accountName)) {
      backgroundColor = accountTypeColors['Asset']
    } else if (accountName && liabilities_list.includes(accountName)) {
      backgroundColor = accountTypeColors['Liability']
    } else if (accountName && equity_list.includes(accountName)) {
      backgroundColor = accountTypeColors['Equity']
    } else if (accountName && revenues_list.includes(accountName)) {
      backgroundColor = accountTypeColors['Revenue']
    } else if (accountName && expenses_list.includes(accountName)) {
      backgroundColor = accountTypeColors['Expense']
    }
    // '-selected' CSS class are darker colors
    return selected ? backgroundColor + '-selected' : backgroundColor
  }

  const MapList = ({ id, selected, map_list, updateFdr }) => (
    <select fdr-id={id} value={selected} className='map-selector' id={`${id}-map-selector`} data-hover='true' onChange={({ target }) => updateFdr(target.value, false, id, selected)} >
      {map_list.map((p, i) => (
        <optgroup label={p[0]} key={i}>
          {p[1].map((c, ii) => (
            <option key={ii}>
              {c}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  )

  const Row = memo(({ hideHistorics, id, match_method, account_type, account_number, extracted_account, historic_matched, xlsx_matched, n_bayes_matched, final_account, edited_by, exact_match, ai_match, ...row }) => {
    const [selected, setSelected] = useState(selections[id] || final_account || xlsx_matched || ai_match);
    let accountTypeClassNames = account_type ? `given-type-cell virtual-cell text-center ${accountTypeColors[account_type]}-selected` : `given-type-cell virtual-cell`
    let finalAccountClassNames = `final-col virtual-cell square-drag ${mapListAccountTypeColor(selected, true)}`
    let matchMethod = user_type === 'Admin' ? matchAbbr[match_method] : null
    const updateFdr = (value, n_bayes_match = false, rowId = null, oldValue = null) => {
      window.selections[id] = value;
      setSelected(value);
      updateRows([id], value, false, n_bayes_match, oldValue);
      if (n_bayes_match) { handleNewNbayesValue(rowId, value) }
    };

    const MatchOverride = ({ styleClass = '', rowId = null, matchType, value, bestMatches = null, row = null }) => {
      // If values from other columns match the final account then make them a darker background color.
      let overRideClassNames = value === selected ? `${styleClass} virtual-cell match-override ${mapListAccountTypeColor(value, true)}` : `${styleClass} virtual-cell match-override ${mapListAccountTypeColor(value)}`

      const handleNbayesMatchDisplay = (rowId, bestMatches) => {
        setBestNbayesMatches(prevState => {
          let newDisplay = null

          if (prevState.rowId === null || prevState.rowId !== rowId) {
            newDisplay = true
          } else if (prevState.rowId === rowId) {
            newDisplay = !prevState.display
          }

          return {
            ...prevState,
            display: newDisplay,
            rowId: rowId,
            bestMatches: bestMatches
          }
        })
      }

      if (matchType !== 'n_bayes' || Object.keys(bestMatches).length === 0) {
        let confidenceDisplay = '';
        if (matchType === 'simple' && row?.xlsx_matched_confidence) {
          confidenceDisplay = ` (${Math.round(row.xlsx_matched_confidence)}%)`;
        }
        return (
          <div
            id={rowId}
            key={rowId}
            className={overRideClassNames}
            onClick={() => updateFdr(value, false, rowId, final_account)}
          >
            {value}{confidenceDisplay}
          </div>
        );
      } else {
        let fontAwesomeStyle = rowId === bestNbayesMatches.rowId && bestNbayesMatches.display ? { 'outline': '1px solid black', 'borderRadius': '3px', 'padding': '1px' } : {}
        let selectedBestMatch = Object.values(bestMatches).find((a) => a['account'] === value)
        let bestMatchPercentage = selectedBestMatch ? `(${Math.round(selectedBestMatch['percentage'])}%)` : ''

        return (
          <div
            id={rowId}
            key={rowId}
            className={overRideClassNames}
          >
            <span onClick={() => updateFdr(value, false, rowId, final_account)}>{`${value} ${bestMatchPercentage}`}</span>
            <div className='match-list-icon'>
              <FontAwesomeIcon style={fontAwesomeStyle} icon={faListUl} onClick={() => handleNbayesMatchDisplay(rowId, bestMatches)} />
              {
                rowId === bestNbayesMatches.rowId && bestNbayesMatches.display
                  ? <div className='nbayes-match-options'>
                    {['Best', 'Better', 'Good'].map((match, i) => {
                      return <div id={`${rowId}-${match}-${i}`} key={`${rowId}-${match}-${i}`} onClick={() => updateFdr(bestMatches[match]['account'], true, rowId, final_account)}>{`${match}: ${bestMatches[match]['account']} ${Math.round(bestMatches[match]['percentage'])}%`}</div>
                    })}
                  </div>
                  : null
              }
            </div>
          </div>
        );
      }
    };

    let rowClassNames = 'virtual-row'

    // if (hideHistorics && hideExactMatches && historic_matched) {
    //   rowClassNames += 'row-with-historic '
    // } else if (hideHistorics && historic_matched && !hideExactMatches) {
    //   if (exact_match) {
    //     rowClassNames = ''
    //   } else {
    //     rowClassNames += 'row-with-historic '
    //   }
    // } else if (hideExactMatches && exact_match && !hideHistorics) {
    //   if (historic_matched) {
    //     rowClassNames = ''
    //   } else {
    //     rowClassNames += 'row-with-exact-match '
    //   }
    // }

    return (
      <div className={rowClassNames} style={{ height: 'auto' }}>
        <div className='edited-by-col virtual-cell'>
          <ul className='edited-by-list'>
            {edited_by && edited_by.map(e => {
              return <li key={Math.random(8)}>{`${e.name} ${moment(e.date).calendar()}`}</li>
            })}
          </ul>
        </div>
        {user_type === 'Admin' ? <div className='match-method-cell virtual-cell text-center'>{matchMethod}</div> : null}
        <div className='account-name-cell virtual-cell text-center'>{account_number}</div>
        <div className={accountTypeClassNames}>{account_type}</div>
        <div className='given-col virtual-cell given'>{extracted_account}</div>
        <MatchOverride styleClass={'historical-col'} matchType={'historic'} value={historic_matched} />
        <MatchOverride styleClass={'best-match-col'} matchType={'simple'} value={xlsx_matched} row={row} />
        <MatchOverride styleClass={'alternatives-col'} rowId={id} matchType={'n_bayes'} value={n_bayes_matched} bestMatches={row.nbayes_top_matches} />
        <MatchOverride styleClass={'alternatives-col'} rowId={id} matchType={'ai_match'} value={ai_match} />
        <div className={finalAccountClassNames} style={{ 'position': 'relative' }} data-hover='true'>
          <MapList id={id} map_list={map_list} selected={selected} updateFdr={updateFdr} />
          <div className='drag-clicker' fdr-id={id} onMouseDown={dragListener} ></div>
        </div>
        {years.map((year, i) =>
          <div key={i} className='year-col virtual-cell'>{parseMoney(row[`year_${year}`])}</div>)}
      </div>
    );
  });

  let display621RevMessage = false

  if (user_type === 'Admin' && naics_code.startsWith('621')) {
    let revenueAccounts = fdrs.filter(r => r.final_account === 'Revenue')
    display621RevMessage = revenueAccounts.length > 0
  }

  const LoadingOverlay = () => {
    return (
      <div className='client-overlay'>
        <LoaderGraphic />
      </div>
    );
  };

  const sortColumns = user_type === 'Admin' ? sortColsAdmin : sortCols;
  const bodyMinWidth = user_type === 'Admin' ? 1509 + (119 * (years.length)) : 1421 + (119 * (years.length));
  const getRowHeight = index => rowHeights[index] || 66;

  const VirtualRow = ({ index, style }) => {
    const rowData = fdrs[index];
    const rowStyles = {
      height: rowData.rowHeight,
      left: 0,
      position: 'absolute',
      top: rowTops[index],
      width: '100%'
    }

    return (
      <div ref={ref} style={{ ...rowStyles, width: bodyMinWidth }}>
        <Row {...rowData} />
      </div>
    );
  };

  return (
    <div>
      {
        user_type === 'Admin' && display621RevMessage
          ? <div style={{ 'textAlign': 'center', 'color': 'red', 'fontSize': '16px' }}>There are still accounts mapped to Revenue.</div>
          : null
      }
      {isFetching ? <LoadingOverlay /> : null}
      <div id='mapping-table-container'>
        <div className='mapping-table-wrapper'>

          <table className='mapping-table-header table-striped table-bordered' ref={headerRef}>
            <thead id='mapper-header'>
              <tr className='mapper-columns-row'>
                <th className='edited-by-col'><div className='mapper-head-cell'><span>Edited By</span></div></th>
                {Object.keys(sortColumns).map(col => {
                  const headerName = sortColumns[col]['name']
                  const styleClass = sortColumns[col]['className']
                  let direction = tableSort[col]
                  let sortArrows = <div className='sort-arrows'><span>&#9650;</span><span>&#9660;</span></div>
                  if (direction === 'asc') {
                    sortArrows = <div className='sort-arrows'><span>&#9650;</span></div>
                  } else if (direction === 'desc') {
                    sortArrows = <div className='sort-arrows'><span>&#9660;</span></div>
                  }
                  return <th className={styleClass} key={col} onClick={() => handleRowSort(col)}><div className='mapper-head-cell'><span>{headerName}</span>{sortArrows}</div></th>
                })}
                {years.map((year, i) => {
                  // On the back end, different fiscal year ends other than Dec 31 (Calendar Year End) are already sorted to the previous year.
                  // However, the year still needs to be the same as seen the file uploads. Ex. Sep 30, 2021 needs to be compared to Dec 31, 2020.
                  let displayYear = fiscalYearEnd === '12/31' ? year : Number(year) + 1
                  let direction = tableSortYears[`year_${year}`]
                  let sortArrows = <div className='sort-arrows'><span>&#9650;</span><span>&#9660;</span></div>
                  if (direction === 'desc') {
                    sortArrows = <div className='sort-arrows'><span>&#9650;</span></div>
                  } else if (direction === 'asc') {
                    sortArrows = <div className='sort-arrows'><span>&#9660;</span></div>
                  }
                  return <th className='year-col' key={`year_${year}`} onClick={() => handleRowSort(`year_${year}`)}><div className='mapper-head-cell'><span>{`${fiscalYearEnd}/${displayYear}`}</span>{sortArrows}</div></th>
                })}
              </tr>
            </thead>
          </table>
          <div ref={bodyRef} style={{ minWidth: bodyMinWidth }} className='virtual-table'>
            <List
              listRef={listRef}
              rowComponent={VirtualRow}
              height={695}
              rowCount={fdrs.length}
              rowHeight={getRowHeight}
              rowProps={{}}
              style={{
                height: 695,
                width: bodyMinWidth,
                borderLeft: '1px solid #ddd',
                overflowX: 'scroll',
                overflowY: 'auto',
              }}
              className='react-window-outer'
              onRowsRendered={(visibleRows) => {
                setLastViewedRow(visibleRows.startIndex);
              }}
            />
          </div>
          <table className='mapping-table-footer table-striped table-bordered' ref={headerRef}>
            <thead id='mapper-header'>
              <tr className='mapper-remainder-row'>
                <th className='edited-by-col'></th>
                {user_type === 'Admin' ? <th className='match-method-col'></th> : null}
                <th className='account-name-col'></th>
                <th className='given-type-col'></th>
                <th className='given-col'></th>
                <th className='historical-col'></th>
                <th className='best-match-col'></th>
                <th className='alternatives-col'></th>
                <th className='alternatives-col'></th>
                <th className='final-col'>Totals:</th>
                {years.map((year, i) => {
                  let yearVal = Number(year)
                  let totalVal = totals?.[yearVal]
                  let colorVal = totalVal !== 0 ? 'red' : 'green'
                  return (<th key={`remainder-${i}`} className='year-col' style={{ color: colorVal }}>{parseMoney(totalVal)}</th>)
                })}
              </tr>
            </thead>
          </table>
        </div>
      </div>
    </div>
  );
});

export default memo(MappingTable, (prevProps, nextProps) => {
  return (
    prevProps.hideHistorics === nextProps.hideHistorics &&
    prevProps.update === nextProps.update &&
    prevProps.handleRowSort === nextProps.handleRowSort &&
    prevProps.isFetching === nextProps.isFetching
  );
});
